import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const TIER_COLORS = {
  BRONZE: { bg: 'bg-orange-900/20', border: 'border-orange-700/40', text: 'text-orange-400', badge: 'bg-orange-600' },
  SILVER: { bg: 'bg-slate-700/20', border: 'border-slate-500/40', text: 'text-slate-300', badge: 'bg-slate-500' },
  GOLD: { bg: 'bg-yellow-900/20', border: 'border-yellow-600/40', text: 'text-yellow-400', badge: 'bg-yellow-500' },
  PLATINUM: { bg: 'bg-cyan-900/20', border: 'border-cyan-600/40', text: 'text-cyan-400', badge: 'bg-cyan-600' },
  DIAMOND: { bg: 'bg-blue-900/20', border: 'border-blue-500/40', text: 'text-blue-300', badge: 'bg-blue-500' },
};

const TIER_ORDER = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'DIAMOND'];

const AchievementCard = ({ achievement, playerAchievement, tiers, isJustUnlocked = false }) => {
  const currentTier = playerAchievement?.tier || null;
  const currentTierIndex = currentTier ? TIER_ORDER?.indexOf(currentTier) : -1;
  const nextTier = TIER_ORDER?.[currentTierIndex + 1] || null;
  const isUnlocked = playerAchievement != null;
  const tierStyle = currentTier ? TIER_COLORS?.[currentTier] : null;

  const currentTierData = tiers?.find((t) => t?.tier === currentTier);
  const nextTierData = tiers?.find((t) => t?.tier === nextTier);

  const rawProgress = nextTierData
    ? Math.min(
        ((playerAchievement?.current_value || 0) / nextTierData?.required_value) * 100,
        100
      )
    : isUnlocked
    ? 100
    : 0;

  const [displayProgress, setDisplayProgress] = useState(rawProgress);
  const [showUnlockGlow, setShowUnlockGlow] = useState(false);
  const [prevValue, setPrevValue] = useState(playerAchievement?.current_value || 0);
  const [isProgressUpdating, setIsProgressUpdating] = useState(false);
  const animFrameRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)')?.matches;

  // Animate progress bar
  useEffect(() => {
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);

    if (isMobile) {
      setDisplayProgress(rawProgress);
      return;
    }

    const start = displayProgress;
    const end = rawProgress;
    const duration = 700;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayProgress(start + (end - start) * eased);
      if (t < 1) animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current); };
  }, [rawProgress]);

  // Detect progress update
  useEffect(() => {
    const newValue = playerAchievement?.current_value || 0;
    if (newValue > prevValue) {
      setIsProgressUpdating(true);
      setPrevValue(newValue);
      setTimeout(() => setIsProgressUpdating(false), 1000);
    }
  }, [playerAchievement?.current_value]);

  // Unlock glow animation
  useEffect(() => {
    if (isJustUnlocked) {
      setShowUnlockGlow(true);
      setTimeout(() => setShowUnlockGlow(false), 3000);
    }
  }, [isJustUnlocked]);

  // Percentage to next tier
  const progressPercent = Math.round(displayProgress);
  const currentValue = playerAchievement?.current_value || 0;
  const requiredValue = nextTierData?.required_value || 0;

  return (
    <div
      className={`rounded-lg border p-4 transition-all duration-300 ${
        isUnlocked
          ? `${tierStyle?.bg} ${tierStyle?.border}`
          : 'bg-card/40 border-border/30 opacity-60'
      }`}
      style={{
        boxShadow: showUnlockGlow
          ? `0 0 20px ${currentTier === 'GOLD' ? 'rgba(234,179,8,0.3)' : currentTier === 'DIAMOND' ? 'rgba(59,130,246,0.3)' : 'rgba(0,217,255,0.2)'}`
          : 'none',
        transition: 'box-shadow 0.5s ease, border-color 0.3s ease',
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isUnlocked ? tierStyle?.bg : 'bg-muted'
          } border ${
            isUnlocked ? tierStyle?.border : 'border-border'
          }`}
          style={{
            transform: showUnlockGlow ? 'scale(1.1)' : 'scale(1)',
            transition: 'transform 0.3s ease',
          }}
        >
          <Icon
            name={achievement?.icon || 'Trophy'}
            size={18}
            className={isUnlocked ? tierStyle?.text : 'text-muted-foreground'}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm text-foreground">{achievement?.achievement_name}</p>
            {currentTier && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded font-bold text-white ${
                  tierStyle?.badge
                }`}
              >
                {currentTier}
              </span>
            )}
            {isJustUnlocked && (
              <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-primary/20 text-primary border border-primary/30 animate-pulse">
                NEW!
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{achievement?.description}</p>
        </div>
      </div>

      {/* Tier Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span className="flex items-center gap-1">
            {nextTier ? `Progress to ${nextTier}` : isUnlocked ? 'Max Tier Reached' : 'Locked'}
            {isProgressUpdating && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            )}
          </span>
          {nextTierData ? (
            <span className={`font-mono transition-colors duration-300 ${
              isProgressUpdating ? 'text-primary font-semibold' : ''
            }`}>
              {currentValue?.toLocaleString()} / {requiredValue?.toLocaleString()}
              <span className="ml-1 text-muted-foreground">({progressPercent}%)</span>
            </span>
          ) : null}
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              isUnlocked ? (tierStyle?.badge || 'bg-primary') : 'bg-muted-foreground/30'
            }`}
            style={{
              width: `${displayProgress}%`,
              transition: isMobile ? 'width 0.4s ease' : 'none',
              willChange: 'width',
              boxShadow: isProgressUpdating ? '0 0 6px rgba(0,217,255,0.5)' : 'none',
            }}
          />
        </div>
      </div>

      {/* Tier Badges */}
      <div className="flex gap-1">
        {TIER_ORDER?.map((tier, idx) => {
          const unlocked = currentTierIndex >= idx;
          const tc = TIER_COLORS?.[tier];
          return (
            <div
              key={tier}
              title={tier}
              className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                unlocked ? tc?.badge : 'bg-muted'
              }`}
            />
          );
        })}
      </div>

      {/* Rewards */}
      {isUnlocked && currentTierData && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/30">
          <div className="flex items-center gap-1">
            <Icon name="Zap" size={11} className="text-yellow-400" />
            <span className="text-xs text-yellow-400">+{currentTierData?.reward_xp?.toLocaleString()} XP</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="DollarSign" size={11} className="text-green-400" />
            <span className="text-xs text-green-400">${currentTierData?.reward_cash?.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <Icon name="Multiply" size={11} className={tierStyle?.text} />
            <span className={`text-xs font-medium ${tierStyle?.text}`}>
              {currentTierData?.tier_multiplier}x
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementCard;
